import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Classes from './RecordTable.module.css';
import useScreenWidth from '../../../../../../Hooks/useScreenWidth';
import SkeletonLoading from '../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading';
import { Pagination, Stack } from '@mui/material';
import TableRow from './TableRow';
import { useNavigate } from 'react-router-dom';
import { AddIcon } from '../../../../../../utils/Icons';
import backend from '../../../../../../api';
import { useSelector } from 'react-redux';
import RefundModal from './RefundModal';

function fmtDate(d) {
  if (!d) return '-';
  const x = new Date(d);
  return x.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
function money(n, c = 'USD') {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: 'currency', currency: c.toUpperCase() || 'USD' });
}

export default function RecordTable() {
  const navigateTo = useNavigate();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;

  const companyInfo = useSelector((state) => state.companyInfo?.info) || {};
  const companyId = companyInfo?._id || '68120023c24987567a7249fe';

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');

  // refund modal state
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundInvoice, setRefundInvoice] = useState(null);
  const [submittingRefund, setSubmittingRefund] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const { data } = await backend.get('/api/invoices/by-company', { params: { companyId } });
      if (data?.ok) setItems(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { let alive = true; (async () => { await load(); })(); return () => { alive = false; }; }, [load]);

  const counts = useMemo(() => {
    const paid = items.filter(i => (i.status || '').toLowerCase() === 'completed').length;
    const unpaid = items.length - paid;
    return { paid, unpaid, all: items.length };
  }, [items]);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const totalPages = Math.ceil(items.length / itemsPerPage) || 1;
  const start = (currentPage - 1) * itemsPerPage;
  const currentItems = useMemo(() => items.slice(start, start + itemsPerPage), [items, start]);
  const startItem = items.length ? start + 1 : 0;
  const endItem = Math.min(start + itemsPerPage, items.length);

  // open refund
  const onRefund = useCallback((inv) => {
    setRefundInvoice(inv);
    setRefundOpen(true);
  }, []);

  // submit refund
  const submitRefund = useCallback(async ({ full, gallons, includeDelivery, includeEmergency }) => {
    if (!refundInvoice?._id) return;
    try {
      setSubmittingRefund(true);
      const body = { full: !!full, gallons: Number(gallons || 0), includeDelivery, includeEmergency };
      const { data } = await backend.post(`/pay/${refundInvoice._id}/refund`, body);
      if (!data?.ok) throw new Error(data?.error || 'Refund failed');

      setRefundOpen(false);
      setRefundInvoice(null);
      await load(); // refresh to pull refundedTotal & net total
      alert(`Refund processed: ${(data?.refund?.status || 'ok')} — ${money((data?.refund?.amount || 0) / 100, refundInvoice.currency || 'USD')}`);
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setSubmittingRefund(false);
    }
  }, [refundInvoice, load]);

  return (
    <div className={Classes.hero}>
      <div className={Classes.table}>
        <div className={Classes.tableWrapper}>

          <div className={Classes.tableBanner}>
            <div className='flexAlign10'>
              <div className={Classes.tableBannerItem}><h5>Paid</h5><h4>{counts.paid}</h4></div>
              <div className={Classes.tableBannerItem}><h5>Unpaid</h5><h4>{counts.unpaid}</h4></div>
              <div className={Classes.tableBannerItem}><h5 className={Classes.allText}>All</h5><h4>{counts.all}</h4></div>
            </div>
            <div>
              <button className={Classes.addBtn} onClick={() => { navigateTo('/add-invoice'); }}>
                <AddIcon height='12px' fill='white' /><h5>Add invoice</h5>
              </button>
            </div>
          </div>

          <div className={Classes.tableHeader}>
            <div className={`${Classes.headerItem} ${Classes.header1}`}><h5>Number</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header2}`}><h5>Client</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header3}`}><h5>Status</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header4}`}><h5>Total (after refunds)</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header5}`}><h5>Created</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header6}`}><h5>Paid</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header7}`} />
          </div>

          <div className={Classes.tableBody}>
            {loading ? (
              <SkeletonLoading length={itemsPerPage} height={isMobile ? '3.5rem' : '2.8rem'} width="100%" br="6px" padding="0" />
            ) : err ? (
              <div style={{ padding: 16, color: 'crimson' }}>{err}</div>
            ) : currentItems.length ? (
              currentItems.map((i) => {
                const refunded = Math.max(0, Number(i?.refundedTotal || 0));
                const net = Math.max(0, Number(i?.total || 0) - refunded);
                return (
                  <TableRow
                    key={i._id}
                    value={{
                      _id: i._id,
                      Number: i.invoiceNumber,
                      Client: i.userId?.name || [i.userId?.firstName, i.userId?.lastName].filter(Boolean).join(' ') || i.userId?.email || '—',
                      Status: (i.status || 'pending').replace(/\b\w/g, m => m.toUpperCase()),
                      Total: money(net, i.currency),      // NET
                      Created: fmtDate(i.issuedAt),
                      Paid: i.paidAt ? fmtDate(i.paidAt) : '-',
                      refundedTotal: refunded,
                      currency: i.currency || 'USD',
                    }}
                    raw={i}
                    onRefund={() => onRefund(i)}
                  />
                );
              })
            ) : (
              <h5 className='placeholderText' style={{ margin: '15px 19px' }} >No invoices yet...</h5>
            )}
          </div>

          {currentItems && currentItems.length > 0 &&
            <div className={Classes.pagFooter}>
              <h5 style={{ height: '1.2rem' }}>
                {loading
                  ? <SkeletonLoading height='1.2rem' width='10rem' br='4px' padding='0' />
                  : `Showing ${startItem} to ${endItem} of ${items.length} results`
                }
              </h5>
              <Stack spacing={2} className={Classes.pagination}>
                {loading
                  ? <SkeletonLoading height='1.2rem' width='5rem' br='4px' padding='0' />
                  : <Pagination count={totalPages} page={currentPage} onChange={(_, p) => setCurrentPage(p)} shape="rounded" />
                }
              </Stack>
            </div>
          }
        </div>
      </div>

      {/* Refund modal (web) */}
      <RefundModal
        open={refundOpen}
        invoice={refundInvoice}
        onClose={() => { if (!submittingRefund) { setRefundOpen(false); setRefundInvoice(null); } }}
        onSubmit={submitRefund}
        submitting={submittingRefund}
      />
    </div>
  );
}
